/**
 * Cliente del asistente de la academia (B8 · §I).
 *
 * Dos superficies muy distintas:
 *
 * - **Pública** (`ask`): la usa gente SIN cuenta, así que no manda cabeceras de
 *   sesión ni cookies de cuenta activa. La identidad es un id de sesión opaco
 *   guardado en `sessionStorage` — no es una credencial, solo agrupa los turnos
 *   de una visita para poder ponerles un tope.
 * - **Administración**: FAQ, preguntas sin respuesta, ajustes y deflexión, con
 *   la auth cookie-BFF de siempre.
 */
import { createMutation, createQuery, useQueryClient } from "@tanstack/solid-query";
import { API_ORIGIN, apiJson, readApiJson } from "@/lib/rest";

const BASE = "/api";
const ANON_KEY = "space.academy-bot.session";

/** Id de sesión anónima, estable dentro de la pestaña. */
export function anonSessionId(): string {
  if (typeof sessionStorage === "undefined") {
    return "";
  }
  const existing = sessionStorage.getItem(ANON_KEY);
  if (existing) {
    return existing;
  }
  const fresh = globalThis.crypto.randomUUID();
  sessionStorage.setItem(ANON_KEY, fresh);
  return fresh;
}

// ─── Público ─────────────────────────────────────────────────────────────────

export interface BotCitation {
  id: string;
  title: string;
  source: string;
}

export interface AskResponse {
  conversationId: string;
  answer: string;
  citations: BotCitation[];
  confidence: number;
  escalated: boolean;
  escalationReason: string | null;
  whatsappUrl: string | null;
  leadCreated: boolean;
}

export interface AskInput {
  orgSlug: string;
  message: string;
  conversationId?: string;
  contactName?: string;
  contactPhone?: string;
}

export const useAskAcademyBot = () =>
  createMutation(() => ({
    mutationFn: async (input: AskInput) => {
      const response = await fetch(`${API_ORIGIN}${BASE}/academy-bot/${input.orgSlug}/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: input.message,
          anonSessionId: anonSessionId(),
          conversationId: input.conversationId,
          contactName: input.contactName,
          contactPhone: input.contactPhone,
        }),
      });
      return readApiJson<AskResponse>(response);
    },
  }));

// ─── Administración ──────────────────────────────────────────────────────────

export interface KbEntry {
  id: string;
  question: string;
  answer: string;
  category: string;
  locale: string;
  source: string;
  helpSlug: string | null;
  position: number;
  published: boolean;
  updatedAt: string;
}

export interface UnansweredQuestion {
  id: string;
  question: string;
  hits: number;
  lastSeenAt: string;
  resolved: boolean;
}

export interface DeflectionStats {
  period: string;
  conversations: number;
  escalated: number;
  anonymous: number;
  deflectionRate: number;
  unansweredOpen: number;
}

export interface BotSettings {
  org: string;
  whatsappPhone: string | null;
  greeting: string | null;
  escalationThreshold: number;
  anonTurnCap: number;
}

const admin = <T>(path: string, init?: RequestInit) =>
  apiJson<T>(`${BASE}/academy-bot${path}`, init);

const keys = {
  entries: (org: string) => ["academy-bot", "entries", org] as const,
  unanswered: (org: string) => ["academy-bot", "unanswered", org] as const,
  deflection: (org: string) => ["academy-bot", "deflection", org] as const,
  settings: (org: string) => ["academy-bot", "settings", org] as const,
};

export const useKbEntries = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: keys.entries(org() ?? ""),
    queryFn: () => admin<KbEntry[]>(`/entries?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useUnanswered = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: keys.unanswered(org() ?? ""),
    queryFn: () =>
      admin<UnansweredQuestion[]>(`/unanswered?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useDeflection = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: keys.deflection(org() ?? ""),
    queryFn: () => admin<DeflectionStats>(`/deflection?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useBotSettings = (org: () => string | undefined) =>
  createQuery(() => ({
    queryKey: keys.settings(org() ?? ""),
    queryFn: () => admin<BotSettings>(`/settings?org=${encodeURIComponent(org() ?? "")}`),
    get enabled() {
      return Boolean(org());
    },
    retry: false,
  }));

export const useUpsertKbEntry = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (entry: Partial<KbEntry> & { question: string; answer: string }) =>
      admin<{ id: string }>("/entries", {
        method: "POST",
        body: JSON.stringify({ ...entry, org: org() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entries(org() ?? "") });
    },
  }));
};

export const useDeleteKbEntry = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (id: string) =>
      admin<{ ok: boolean }>(`/entries/${id}?org=${encodeURIComponent(org() ?? "")}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.entries(org() ?? "") });
    },
  }));
};

/**
 * Convierte una pregunta sin respuesta en una entrada de FAQ. Es el bucle que
 * hace que el chatbot mejore: cada fallo se transforma en una línea de la KB.
 */
export const useResolveUnanswered = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (input: { id: string; entryId?: string }) =>
      admin<{ ok: boolean }>(`/unanswered/${input.id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ org: org(), entryId: input.entryId }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.unanswered(org() ?? "") });
      void qc.invalidateQueries({ queryKey: keys.deflection(org() ?? "") });
    },
  }));
};

export const useSaveBotSettings = (org: () => string | undefined) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (settings: Omit<BotSettings, "org">) =>
      admin<{ ok: boolean }>("/settings", {
        method: "POST",
        body: JSON.stringify({ ...settings, org: org() }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.settings(org() ?? "") });
    },
  }));
};
